import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack, Typography } from '@mui/material';
import { useListContext, useTranslate } from 'ra-core';
import { SavedQuery, extractValidSavedQueries, useSavedQueries } from 'ra-ui-materialui';
import { useCallback, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import { RemoveSavedQueryDialog } from './RemoveSavedQueryDialog';

interface RemoveSavedQueryButtonProps {
  query?: SavedQuery;
}

function RemoveSavedQueryButton(props: RemoveSavedQueryButtonProps) {
  const { query } = props;
  const [open, setOpen] = useState(false);
  const toggleDialog = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);
  const translate = useTranslate();

  return (
    <>
      <Button size="small" color="primary" onClick={toggleDialog}>
        {translate('ra.action.delete')}
      </Button>
      <RemoveSavedQueryDialog open={open} onClose={toggleDialog} filterToRemove={query} />
    </>
  );
}

interface SavedFiltersListProps {
  closeSidebar: () => void;
}

function SavedFiltersList(props: SavedFiltersListProps) {
  const { closeSidebar } = props;
  const { resource, setFilters, setSort, setPage, setPerPage } = useListContext();
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const [savedQueries] = useSavedQueries(resource);
  const validSavedQueries = extractValidSavedQueries(savedQueries);
  const translate = useTranslate();

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handleApplyFilter = useCallback(
    (props: any) => {
      const { value } = props;
      // @ts-ignore
      setFilters(value.filter);
      setSort({ field: value.sort.field, order: value.sort.order });
      setPage(1);
      setPerPage(value.perPage);
      closeSidebar();
    },
    [setFilters, setSort, setPage, setPerPage, closeSidebar]
  );

  return savedQueries && savedQueries.length > 0 ? (
    <Box
      sx={{
        mt: '-1px',
        mb: 2,
        '& .MuiAccordion-root': {
          borderColor: theme.palette.divider,
          '& .Mui-expanded:last-of-type': {
            marginBottom: 'inherit'
          },
          '& .MuiAccordionSummary-root': {
            bgcolor: 'transparent',
            flexDirection: 'row'
          },
          '& .MuiAccordionSummary-content': {
            justifyContent: 'space-between',
            marginLeft: 0
          },
          '& .MuiAccordionDetails-root': {
            borderColor: theme.palette.divider
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: theme.palette.text.primary,
            '&.Mui-expanded': {
              transform: 'rotate(180deg)'
            }
          }
        }
      }}
    >
      <Accordion expanded={expanded} onChange={toggleExpanded}>
        <AccordionSummary sx={{ px: '20px' }} expandIcon={<ExpandMoreIcon fontSize="small" />}>
          <Typography variant="h5" fontWeight="bold">
            {translate('ra.action.saved_filters')} ({validSavedQueries.length})
          </Typography>
        </AccordionSummary>
        {validSavedQueries?.map((query, i) => (
          <AccordionDetails
            key={i}
            sx={{ display: 'flex', justifyContent: 'space-between', px: '20px', alignItems: 'center' }}
          >
            <Typography variant="body1">{query.label}</Typography>
            <Stack flexDirection="row" alignItems={'center'}>
              <RemoveSavedQueryButton query={query} />
              <Button size="small" color="primary" onClick={() => handleApplyFilter({ value: query.value })}>
                {translate('ra.action.apply')}
              </Button>
            </Stack>
          </AccordionDetails>
        ))}
      </Accordion>
    </Box>
  ) : (
    <Box height={16} />
  );
}

export { SavedFiltersList };
